import React, { useEffect, useMemo } from 'react';
import {
  Navigate,
  Outlet,
  useLocation,
  useMatch,
  useParams,
} from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { TooltipProvider } from '@/shared/ui/tooltip';
import { SidebarInset, SidebarProvider } from '@/shared/ui/sidebar';
import { cn } from '@/shared/lib/cn';
import { useCapabilities } from './providers/CapabilitiesProvider';
import { useAgentCatalog } from './providers/AgentCatalogProvider';
import { ThemeProvider } from '../contexts/ThemeContext';
import { AgentSidebarWidget } from '../widgets/agent-sidebar';
import ChatPanels from '../components/ChatPanels';
import { AgentSelectorWidget } from '../widgets/agent-selector';
import ErrorBoundary from '../components/ErrorBoundary';
import { useSettingsAccess } from '../components/settings/access';
import { SessionDialogs } from './SessionDialogs';
import { useSlackReturnHandler } from '@/features/plugins/slack';
import { useOAuthReturnHandler } from '@/features/plugins/connect';
import { Toaster } from '@/shared/ui/sonner';
import { useSidebarLayoutStore } from '@/shared/stores/sidebarLayout';
import { useIsMobile } from '@/shared/lib/hooks/use-mobile';
import {
  useChatUrlSync,
  useChatViewStore,
  useChatWorkspaceController,
  useChatWorkspaceStore,
} from '@/shared/chat-workspace';

interface MainViewProps {
  selectedAgentId: string | null;
  onSelectedAgentChange: (agentId: string | null) => void;
}

function MainView({ selectedAgentId, onSelectedAgentChange }: MainViewProps) {
  const resetGeneration = useChatWorkspaceStore((state) => state.resetGeneration);
  return (
    <main className="flex min-h-0 flex-1 overflow-hidden">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <AgentSelectorWidget
          selectedAgentId={selectedAgentId}
          onAgentSelect={onSelectedAgentChange}
        />
        <ChatPanels selectedAgentId={selectedAgentId} resetGeneration={resetGeneration} />
      </div>
    </main>
  );
}

export function SettingsIndexRedirect() {
  const { canAccessAdmin, visibleAgents, canAccessPlugins } = useSettingsAccess();
  if (canAccessAdmin) return <Navigate to="/settings/admin" replace />;
  if (visibleAgents.length > 0) {
    return (
      <Navigate
        to={`/settings/agents/${visibleAgents[0].id}/config`}
        replace
      />
    );
  }
  if (canAccessPlugins) return <Navigate to="/plugins" replace />;
  return <Navigate to="/" replace />;
}

export function AgentTabsIndexRedirect() {
  const { agentId } = useParams<{ agentId: string }>();
  if (!agentId) return <Navigate to="/settings/agents" replace />;
  return <Navigate to={`/settings/agents/${agentId}/config`} replace />;
}

/**
 * Inner shell — holds all AppContent logic. Rendered inside ThemeProvider
 * so the shell and chat workspace share the same top-level providers.
 */
function AppShellInner() {
  const { pathname } = useLocation();
  const {
    loading: authLoading,
    status: authStatus,
    canSwitchDevPersona,
    devPersona,
    switchDevPersona,
  } = useCapabilities();
  const { agents, agentsLoading, agentsError, refreshCatalog } = useAgentCatalog();
  const { selectAgent, pruneAgents } = useChatWorkspaceController();
  const selectedAgentId = useChatViewStore((state) => state.selectedAgentId);
  // Chat routes ('/', '/agents/*') render a null Outlet element — MainView is
  // the always-mounted page there. Every other routed page (settings,
  // schedules, skills) renders real content into the Outlet and must fill the column.
  const landingMatch = useMatch('/');
  const agentRouteMatch = useMatch('/agents/*');
  const isOutletRoute = landingMatch == null && agentRouteMatch == null;

  // Session expiry is enforced by the BFF, not the browser. A 401 on any
  // protected request triggers an immediate logout (see
  // shared/auth/logout.ts#handleSessionExpired) instead of an in-app dialog
  // or a navigation to /session-expired.

  // Handle the post-OAuth return (?slack=connected|error).
  useSlackReturnHandler();
  // Handle the generic per-provider OAuth return (?<provider>=connected|error)
  // — full-redirect fallback only; the popup path is handled in the bootstrap.
  useOAuthReturnHandler();

  // Re-fetch agent catalog when dev persona changes (groups change → different agents visible)
  const devPersonaRef = React.useRef(devPersona);
  useEffect(() => {
    if (devPersonaRef.current !== devPersona) {
      devPersonaRef.current = devPersona;
      refreshCatalog();
    }
  }, [devPersona, refreshCatalog]);

  // Sidebar collapsed/expanded state — persisted so it survives reloads. A
  // narrow viewport collapses to the same icon rail a route-driven collapse
  // uses — one standard for "narrow" at every width, never a hidden sidebar.
  const sidebarCollapsed = useSidebarLayoutStore((state) => state.collapsed);
  const setSidebarCollapsed = useSidebarLayoutStore((state) => state.setCollapsed);
  const isNarrowViewport = useIsMobile();

  useEffect(() => {
    setSidebarCollapsed(isNarrowViewport || pathname === '/' || pathname.startsWith('/settings'));
  }, [isNarrowViewport, pathname, setSidebarCollapsed]);

  // Deep-link agent + session in the URL.
  useChatUrlSync(true);

  const agentIds = useMemo(() => new Set(agents.map((a) => a.id)), [agents]);
  useEffect(() => {
    if (authLoading) return;
    if (authStatus !== 'ready') return;
    if (agentsLoading) return;
    if (agentsError) return;
    pruneAgents(Array.from(agentIds));
  }, [agentIds, agentsError, agentsLoading, authLoading, authStatus, pruneAgents]);

  // The logo lives inside a fixed-width block that mirrors the sidebar area.

  // Back button: navigate to the agent selector while preserving all thread state.
  // Returning to the same agent resumes the active chat (including any in-progress stream).

  if (authLoading) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <Loader2 className="size-6 animate-spin" aria-label="Loading application" />
        <span>Loading...</span>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <SidebarProvider
        open={!sidebarCollapsed}
        onOpenChange={(open) => setSidebarCollapsed(!open)}
        className="@container h-dvh min-h-0 overflow-hidden"
      >
        <AgentSidebarWidget
          selectedAgentId={selectedAgentId}
          canSwitchDevPersona={canSwitchDevPersona}
          devPersona={devPersona}
          onSwitchDevPersona={(key) => { void switchDevPersona(key); }}
        />
        <SidebarInset className="min-h-0 overflow-hidden bg-background transition-[margin,border-radius,box-shadow] duration-200 ease-linear">
          {/* MainView always mounted — hidden on any routed page (settings,
              schedules) so its SSE streams survive while the Outlet page below
              takes the full column. Visible only on the chat/landing route. */}
          <div
            className={cn(
              'flex min-h-0 flex-1 flex-col overflow-hidden bg-background',
              isOutletRoute && 'hidden',
            )}
          >
            <MainView
              selectedAgentId={selectedAgentId}
              onSelectedAgentChange={selectAgent}
            />
          </div>

          <div
            className={cn(
              isOutletRoute ? 'min-h-0 min-w-0 flex-1 overflow-hidden bg-background' : 'hidden',
            )}
          >
            <Outlet />
          </div>
        </SidebarInset>
        <SessionDialogs />
      </SidebarProvider>
    </TooltipProvider>
  );
}

/**
 * AppShell — React Router v6 layout route component.
 *
 * Wraps AppShellInner with ThemeProvider so useTheme() resolves inside.
 */
export function AppShell() {
  return (
    <ThemeProvider>
      <ErrorBoundary>
        <AppShellInner />
        <Toaster />
      </ErrorBoundary>
    </ThemeProvider>
  );
}
